import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import { toast, undoToast } from '../ui'
import type { Beat, Comment, Media, ProjectData, Select, Take, Transcript } from '../types'

/*
  One project's state: loaded once, then kept fresh by polling /changes every
  few seconds (selects, beats and comments other people add show up live).
  Writes are optimistic and roll back with a toast if the server says no.
*/
export function useProject(projectId: string) {
  const [data, setData] = useState<ProjectData | null>(null)
  const [transcripts, setTranscripts] = useState<Record<string, Transcript>>({})
  const [error, setError] = useState<{ status: number; message: string } | null>(null)
  const since = useRef(0)
  // This viewer's look changes in flight, and when the last one settled (polls older than that are stale for the look).
  const lookPending = useRef(0)
  const lookTouched = useRef(0)
  const busy = useRef(false)
  busy.current = !!data?.jobs.some((j) => j.status === 'queued' || j.status === 'running')
  const transcriptStamp = useRef<Record<string, number>>({})

  const loadTranscripts = useCallback(async () => {
    const r = await api.get<{ transcripts: Record<string, Transcript> }>(`p/${projectId}/transcripts`)
    for (const [k, v] of Object.entries(r.transcripts)) transcriptStamp.current[k] = v.updated_at
    setTranscripts(r.transcripts)
  }, [projectId])

  const load = useCallback(async () => {
    try {
      const [d] = await Promise.all([api.get<ProjectData>(`p/${projectId}`), loadTranscripts()])
      since.current = d.serverTime
      setData({ ...d, selects: d.selects.map(normSelect) })
    } catch (e) {
      setError(e instanceof ApiError ? { status: e.status, message: e.message } : { status: 0, message: 'Could not load the project.' })
    }
  }, [projectId, loadTranscripts])

  useEffect(() => {
    load()
  }, [load])

  // Live updates.
  useEffect(() => {
    if (!data) return
    let stop = false
    let fullEvery = 0
    const tick = async () => {
      if (stop || document.hidden) return
      const asked = Date.now()
      try {
        const ch = await api.get<{ selects: Select[]; beats: Beat[]; comments: Comment[]; takes: { id: string; status: Take['status']; error: string | null; transcript_updated: number | null }[]; look?: { look: string; look_client: number; look_film: number; look_cams: string } | null; serverTime: number }>(
          `p/${projectId}/changes?since=${since.current - 2000}`,
        )
        since.current = ch.serverTime
        let needTranscripts = false
        let needFull = false
        setData((d) => {
          if (!d) return d
          const selects = mergeById(d.selects, ch.selects.map(normSelect)).filter((s) => !s.deleted)
          const beats = mergeById(d.beats, ch.beats).filter((b) => !b.deleted).sort((a, b) => a.sort - b.sort)
          const comments = mergeById(d.comments, ch.comments).filter((c) => !c.deleted)
          const byId = new Map(ch.takes.map((t) => [t.id, t]))
          if (ch.takes.length !== d.takes.length) needFull = true
          const takes = d.takes.map((t) => {
            const u = byId.get(t.id)
            if (u?.transcript_updated && u.transcript_updated !== transcriptStamp.current[t.id]) needTranscripts = true
            return u && (u.status !== t.status || u.error !== t.error) ? { ...t, status: u.status, error: u.error } : t
          })
          // A poll that left before this viewer's own look change settled carries the old look: skip it.
          const lk = ch.look && !lookPending.current && lookTouched.current < asked ? ch.look : null
          const project = lk && (lk.look !== d.project.look || lk.look_client !== d.project.look_client || lk.look_film !== d.project.look_film || lk.look_cams !== d.project.look_cams) ? { ...d.project, ...lk } : d.project
          return { ...d, project, selects, beats, comments, takes }
        })
        fullEvery++
        // Team: jobs refresh every tick while work is running, so progress and time left stay live.
        if (needFull || (data.viewer.kind === 'team' && (busy.current || fullEvery % 6 === 0))) {
          const d = await api.get<ProjectData>(`p/${projectId}`)
          setData((cur) => (cur ? { ...cur, takes: d.takes, media: d.media, jobs: d.jobs, workers: d.workers } : d))
        }
        if (needTranscripts) await loadTranscripts()
      } catch {
        /* offline for a moment: try again next tick */
      }
    }
    const iv = setInterval(tick, 5000)
    const vis = () => !document.hidden && tick()
    document.addEventListener('visibilitychange', vis)
    return () => {
      stop = true
      clearInterval(iv)
      document.removeEventListener('visibilitychange', vis)
    }
  }, [!!data, projectId, loadTranscripts]) // eslint-disable-line react-hooks/exhaustive-deps

  const media = useMemo(() => new Map<string, Media>((data?.media || []).map((m) => [m.id, m])), [data?.media])
  const takes = useMemo(() => new Map<string, Take>((data?.takes || []).map((t) => [t.id, t])), [data?.takes])

  const patchLocal = (fn: (d: ProjectData) => ProjectData) => setData((d) => (d ? fn(d) : d))

  const actions = useMemo(() => {
    const guard = async <T,>(p: Promise<T>, undo?: () => void): Promise<T | null> => {
      try {
        return await p
      } catch (e) {
        undo?.()
        toast(e instanceof ApiError ? e.message : 'That did not save. Try again.', 'err')
        return null
      }
    }
    return {
      async addSelect(s: Partial<Select> & { take_id: string; start: number; end: number; text: string }) {
        const r = await guard(api.post<{ select: Select }>(`p/${projectId}/selects`, s))
        if (r) patchLocal((d) => ({ ...d, selects: mergeById(d.selects, [normSelect(r.select)]) }))
        return r ? normSelect(r.select) : null
      },
      async updateSelect(id: string, patch: Partial<Select>) {
        let prev: Select | undefined
        patchLocal((d) => ({ ...d, selects: d.selects.map((s) => (s.id === id ? ((prev = s), { ...s, ...patch }) : s)) }))
        const r = await guard(api.patch<{ select: Select }>(`p/${projectId}/selects/${id}`, patch), () =>
          patchLocal((d) => ({ ...d, selects: d.selects.map((s) => (s.id === id && prev ? prev : s)) })),
        )
        if (r) patchLocal((d) => ({ ...d, selects: mergeById(d.selects, [normSelect(r.select)]) }))
      },
      async deleteSelect(id: string) {
        let prev: Select | undefined
        patchLocal((d) => ({ ...d, selects: d.selects.filter((s) => (s.id === id ? ((prev = s), false) : true)) }))
        const ok = await guard(api.del(`p/${projectId}/selects/${id}`), () => prev && patchLocal((d) => ({ ...d, selects: [...d.selects, prev!] })))
        if (ok && prev) {
          undoToast('Line removed', async () => {
            const r = await guard(api.post<{ select: Select }>(`p/${projectId}/selects/${id}/restore`))
            if (r) patchLocal((d) => ({ ...d, selects: mergeById(d.selects, [normSelect(r.select)]) }))
          })
        }
      },
      async addBeat(title: string, description = '') {
        const r = await guard(api.post<{ beat: Beat }>(`p/${projectId}/beats`, { title, description }))
        if (r) patchLocal((d) => ({ ...d, beats: mergeById(d.beats, [r.beat]).sort((a, b) => a.sort - b.sort) }))
        return r?.beat || null
      },
      async updateBeat(id: string, patch: Partial<Beat>) {
        let prev: Beat | undefined
        patchLocal((d) => ({ ...d, beats: d.beats.map((b) => (b.id === id ? ((prev = b), { ...b, ...patch }) : b)).sort((a, b) => a.sort - b.sort) }))
        await guard(api.patch(`p/${projectId}/beats/${id}`, patch), () => patchLocal((d) => ({ ...d, beats: d.beats.map((b) => (b.id === id && prev ? prev : b)).sort((a, b) => a.sort - b.sort) })))
      },
      async deleteBeat(id: string) {
        const snapshot = data
        const had = (data?.selects || []).filter((s) => s.chapters.includes(id)).map((s) => ({ id: s.id, chapters: s.chapters, beat_sort: s.beat_sort }))
        const title = data?.beats.find((b) => b.id === id)?.title || 'Chapter'
        patchLocal((d) => ({ ...d, beats: d.beats.filter((b) => b.id !== id), selects: d.selects.map((s) => (s.chapters.includes(id) || s.beat_id === id ? { ...s, beat_id: s.beat_id === id ? null : s.beat_id, chapters: s.chapters.filter((c) => c !== id) } : s)) }))
        const ok = await guard(api.del(`p/${projectId}/beats/${id}`), () => snapshot && setData(snapshot))
        if (!ok) return
        undoToast(`Removed "${title}"`, async () => {
          const r = await guard(api.post<{ beat: Beat }>(`p/${projectId}/beats/${id}/restore`))
          if (!r) return
          patchLocal((d) => ({ ...d, beats: mergeById(d.beats, [r.beat]).sort((a, b) => a.sort - b.sort) }))
          for (const h of had) {
            patchLocal((d) => ({ ...d, selects: d.selects.map((s) => (s.id === h.id ? { ...s, chapters: [...new Set([...s.chapters, ...h.chapters])], beat_sort: h.beat_sort } : s)) }))
            const cur = await guard(api.patch<{ select: Select }>(`p/${projectId}/selects/${h.id}`, { chapters: h.chapters, beat_sort: h.beat_sort }))
            if (cur) patchLocal((d) => ({ ...d, selects: mergeById(d.selects, [normSelect(cur.select)]) }))
          }
        })
      },
      async addComment(selectId: string, body: string) {
        const r = await guard(api.post<{ comment: Comment }>(`p/${projectId}/selects/${selectId}/comments`, { body }))
        if (r) patchLocal((d) => ({ ...d, comments: mergeById(d.comments, [r.comment]) }))
      },
      async deleteComment(id: string) {
        patchLocal((d) => ({ ...d, comments: d.comments.filter((c) => c.id !== id) }))
        await guard(api.del(`p/${projectId}/comments/${id}`))
      },
      async renameSpeaker(takeIds: string[], raw: string, name: string) {
        for (const tid of takeIds) {
          const take = data?.takes.find((t) => t.id === tid)
          if (!take) continue
          const speakers = { ...take.speakers, [raw]: name }
          patchLocal((d) => ({ ...d, takes: d.takes.map((t) => (t.id === tid ? { ...t, speakers } : t)) }))
          await guard(api.patch(`p/${projectId}/takes/${tid}`, { speakers }))
        }
      },
      async saveTranscript(takeId: string, tr: Transcript) {
        const r = await guard(api.put<{ version: number }>(`p/${projectId}/takes/${takeId}/transcript`, { segments: tr.segments, version: tr.version }))
        if (r) {
          const next = { ...tr, version: r.version, updated_at: Date.now() }
          transcriptStamp.current[takeId] = next.updated_at
          setTranscripts((cur) => ({ ...cur, [takeId]: next }))
          return true
        }
        return false
      },
      async setHidden(takeIds: string[], hidden: boolean) {
        patchLocal((d) => ({ ...d, takes: d.takes.map((t) => (takeIds.includes(t.id) ? { ...t, hidden: hidden ? 1 : 0 } : t)) }))
        for (const tid of takeIds) await guard(api.patch(`p/${projectId}/takes/${tid}`, { hidden }))
      },
      async renameInterview(takeIds: string[], name: string) {
        patchLocal((d) => ({ ...d, takes: d.takes.map((t) => (takeIds.includes(t.id) ? { ...t, interview: name } : t)) }))
        await guard(api.post(`p/${projectId}/interviews/rename`, { take_ids: takeIds, name }))
      },
      /** The Osmo look: the look ('' = as shot), Filmic or clean, whether clients may pick them, camera profiles ({A: key | 'auto' | 'none'}). */
      async setLook(change: { look?: string; film?: boolean; client?: boolean; cams?: Record<string, string> }) {
        // Shows at once; the server's answer (or the old state, if it fails) settles it.
        const before = data?.project
        lookPending.current++
        patchLocal((d) => {
          const cams: Record<string, string> = JSON.parse(d.project.look_cams || '{}')
          for (const [l, k] of Object.entries(change.cams || {})) if (k === 'auto') delete cams[l]; else cams[l] = k
          return { ...d, project: { ...d.project, look: change.look ?? d.project.look, look_client: change.client == null ? d.project.look_client : change.client ? 1 : 0, look_film: change.film == null ? d.project.look_film : change.film ? 1 : 0, look_cams: JSON.stringify(cams) } }
        })
        try {
          const r = await api.patch<{ look: string; look_client: number; look_film: number; look_cams: Record<string, string> }>(`p/${projectId}/look`, change)
          patchLocal((d) => ({ ...d, project: { ...d.project, look: r.look, look_client: r.look_client, look_film: r.look_film, look_cams: JSON.stringify(r.look_cams) } }))
          lookPending.current--
          lookTouched.current = Date.now()
          return r
        } catch (e) {
          lookPending.current--
          lookTouched.current = Date.now()
          if (before) patchLocal((d) => ({ ...d, project: { ...d.project, look: before.look, look_client: before.look_client, look_film: before.look_film, look_cams: before.look_cams } }))
          throw e
        }
      },
      reload: load,
    }
  }, [projectId, data, load])

  return { data, transcripts, error, media, takes, actions }
}

export function normSelect(x: Select): Select {
  const raw = (x as unknown as { chapters: unknown }).chapters
  let chapters: string[] = []
  if (Array.isArray(raw)) chapters = raw as string[]
  else if (typeof raw === 'string') {
    try {
      chapters = JSON.parse(raw)
    } catch {
      chapters = []
    }
  }
  if (!chapters.length && x.beat_id) chapters = [x.beat_id]
  return { ...x, chapters }
}

function mergeById<T extends { id: string }>(cur: T[], incoming: T[]): T[] {
  if (!incoming.length) return cur
  const map = new Map(cur.map((x) => [x.id, x]))
  for (const x of incoming) map.set(x.id, { ...map.get(x.id), ...x })
  return [...map.values()]
}

/*
  The playhead, shared without re-rendering the whole tree every frame:
  components subscribe and update their own DOM.
*/
export type Playhead = { takeId: string | null; time: number; playing: boolean; mediaId: string | null }
export function createPlayhead() {
  let state: Playhead = { takeId: null, time: 0, playing: false, mediaId: null }
  const subs = new Set<(p: Playhead) => void>()
  return {
    get: () => state,
    set(p: Partial<Playhead>) {
      state = { ...state, ...p }
      subs.forEach((f) => f(state))
    },
    subscribe(f: (p: Playhead) => void) {
      subs.add(f)
      return () => {
        subs.delete(f)
      }
    },
  }
}
export type PlayheadStore = ReturnType<typeof createPlayhead>
